import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DoctorContext } from "../../../stores/Doctor/doctor.provider";
import useStore from "../../../helpers/useStore";
import { ClinicData } from "../DoctorTabContent/doctor-client-types";
import LoadingSpinner from "../../BIDashboard/components/LoadingSpinner";
import GenericInfiniteTable, {
  ColumnDef,
} from "../../../molecules/PatientRxTables/GenericInfiniteTable";

/**
 * One table row: a clinic paired with one of its doctors. The first row of a
 * clinic carries the clinic columns; follow-up rows only fill the doctor
 * columns. `clinicIndex` drives the clinic-by-clinic alternating shading.
 */
interface ClinicDoctorRow {
  id: string;
  clinic: ClinicData;
  doctor: any | null;
  isFirstRow: boolean;
  clinicIndex: number;
}

export default function ClinicTable({
  searchDto,
  searchValue,
  refresh,
  openDialogForEdit,
  toolbar,
}: {
  searchDto: any;
  searchValue: string;
  refresh: boolean;
  openDialogForEdit: (clinicId: string) => void;
  toolbar?: React.ReactNode;
}) {
  const DoctorStore = useStore(DoctorContext);
  const { getAllClinics, searchClinic, removeClinic, toggleAssignmentActive } = DoctorStore;

  const PAGE_SIZE = 25;
  const [clinicData, setClinicData] = React.useState<ClinicData[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [openDeleteClinicDialog, setOpenDeleteClinicDialog] = useState<boolean>(false);
  const [clinicToDelete, setClinicToDelete] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const pageRef = useRef(1);

  const [selectedIndex, setSelectedIndexState] = useState<number | null>(null);
  const setSelectedIndex = (index: number | ((prevIndex: number) => number)) =>
    setSelectedIndexState((prev) => (typeof index === "function" ? index(prev ?? -1) : index));

  const mapClinicResponse = (data: any[]): ClinicData[] =>
    data.map((clinic: any) => {
      const rawDoctors = clinic.doctorAssignments
        ? clinic.doctorAssignments.map((a: any) => ({ ...a.doctor, assignmentId: a.id, isActive: a.isActive }))
        : clinic.doctors || [];
      return {
        id: clinic.id,
        BSNR: (clinic as any).BSNR,
        businessName: clinic.contactInfo?.businessName,
        phone1: clinic.contactInfo?.phone1,
        fax: clinic.contactInfo?.fax,
        addressLine1: clinic.contactInfo?.addressLine1,
        city: clinic.contactInfo?.city,
        zip: clinic.contactInfo?.zip,
        email: clinic.contactInfo?.email,
        notes: clinic.notes,
        contactInfo: clinic.contactInfo,
        methodForDrReport: clinic.methodForDrReport,
        methodForRxOrder: clinic.methodForRxOrder,
        doctors: rawDoctors.map((doctor: any) => ({
          id: doctor.id,
          LANR: doctor.LANR,
          lastName: doctor.contactInfo?.lastName,
          firstName: doctor.contactInfo?.firstNames,
          title: doctor.contactInfo?.salutation,
          assignmentId: doctor.assignmentId,
          isActive: doctor.isActive ?? true,
        })),
      };
    }) as ClinicData[];

  const fetchRequestId = useRef(0);

  const fetchPage = useCallback(async (page: number, append: boolean) => {
    const requestId = ++fetchRequestId.current;
    if (append) setIsFetchingNextPage(true);
    else setIsInitialLoading(true);
    try {
      const response = searchValue
        ? await searchClinic(page, PAGE_SIZE, searchDto)
        : await getAllClinics(page, PAGE_SIZE);
      // Ignore stale responses
      if (requestId !== fetchRequestId.current) return;
      const mapped = mapClinicResponse(response.data);
      setClinicData((prev) => append ? [...prev, ...mapped] : mapped);
      const totalLoaded = append ? (page - 1) * PAGE_SIZE + mapped.length : mapped.length;
      setHasMore(totalLoaded < response.meta.totalItems);
    } catch (error) {
      if (requestId !== fetchRequestId.current) return;
      console.error("Error fetching clinics:", error);
    } finally {
      if (requestId === fetchRequestId.current) {
        setIsFetchingNextPage(false);
        setIsInitialLoading(false);
      }
    }
  }, [searchValue, searchDto, getAllClinics, searchClinic]);

  // Reset when search/refresh changes
  useEffect(() => {
    pageRef.current = 1;
    setClinicData([]);
    setHasMore(true);
    fetchPage(1, false);
  }, [searchValue, refresh, fetchPage]);

  const fetchMoreData = () => {
    pageRef.current += 1;
    fetchPage(pageRef.current, true);
  };

  const handleDeleteClinic = async (clinicRow: any) => {
    setClinicToDelete(clinicRow);
    setOpenDeleteClinicDialog(true);
  };

  const handleDeleteClinicFunc = async () => {
    setOpenDeleteClinicDialog(false);
    setLoading(true);
    try {
      await removeClinic(clinicToDelete?.id || clinicToDelete);
      setClinicData((prevClinicData) =>
        prevClinicData.filter((clinic) => clinic.id !== (clinicToDelete?.id || clinicToDelete))
      );
    } catch (error) {
      console.error("Error removing clinic:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAssignment = async (clinicId: string, doctor: any) => {
    try {
      await toggleAssignmentActive(doctor.assignmentId, !doctor.isActive);
      // Update the toggled assignment in place so the loaded pages and the
      // scroll position are preserved (previously this reset to page 1).
      setClinicData((prev) =>
        prev.map((clinic) =>
          clinic.id !== clinicId
            ? clinic
            : {
                ...clinic,
                doctors: clinic.doctors.map((d: any) =>
                  d.assignmentId === doctor.assignmentId ? { ...d, isActive: !d.isActive } : d
                ),
              }
        )
      );
    } catch (e) {
      console.error("Error toggling assignment:", e);
    }
  };

  // ---- Flatten clinics into one row per doctor -----------------------------
  const rows: ClinicDoctorRow[] = useMemo(() => {
    const out: ClinicDoctorRow[] = [];
    clinicData.forEach((clinic, clinicIndex) => {
      const doctors = clinic.doctors.length > 0 ? clinic.doctors : [null];
      doctors.forEach((doctor: any, index: number) => {
        out.push({
          id: `${clinic.id}-${doctor?.assignmentId ?? doctor?.id ?? index}`,
          clinic,
          doctor,
          isFirstRow: index === 0,
          clinicIndex,
        });
      });
    });
    return out;
  }, [clinicData]);

  const columns: ColumnDef<ClinicDoctorRow>[] = useMemo(
    () => [
      { key: "BSNR", header: "BSNR", render: (r) => (r.isFirstRow ? r.clinic.BSNR || "" : "") },
      {
        key: "businessName",
        header: "Praxisname",
        render: (r) => (r.isFirstRow ? r.clinic.businessName || "" : ""),
      },
      { key: "phone1", header: "Telefon", render: (r) => (r.isFirstRow ? r.clinic.phone1 || "" : "") },
      { key: "fax", header: "Fax", render: (r) => (r.isFirstRow ? r.clinic.fax || "" : "") },
      {
        key: "addressLine1",
        header: "Adresse",
        render: (r) => (r.isFirstRow ? r.clinic.addressLine1 || "" : ""),
      },
      { key: "city", header: "Ort", render: (r) => (r.isFirstRow ? r.clinic.city || "" : "") },
      { key: "email", header: "Email", render: (r) => (r.isFirstRow ? r.clinic.email || "" : "") },
      { key: "notes", header: "Hinweise", render: (r) => (r.isFirstRow ? r.clinic.notes || "" : "") },
      {
        key: "methodForDrReport",
        header: "Kontaktmethode (Dr Report)",
        render: (r) => (r.isFirstRow ? r.clinic.methodForDrReport || "" : ""),
      },
      {
        key: "methodForRxOrder",
        header: "Kontaktmethode (Rx Order)",
        render: (r) => (r.isFirstRow ? r.clinic.methodForRxOrder || "" : ""),
      },
      { key: "LANR", header: "LANR", render: (r) => r.doctor?.LANR || "" },
      { key: "title", header: "Titel", render: (r) => r.doctor?.title || "" },
      { key: "firstName", header: "Vorname", render: (r) => r.doctor?.firstName || "" },
      { key: "lastName", header: "Name", render: (r) => r.doctor?.lastName || "" },
      {
        key: "isActive",
        header: "Aktiv",
        render: (r) =>
          r.doctor?.assignmentId ? (
            <Switch
              size="small"
              checked={r.doctor.isActive !== false}
              onClick={(e) => e.stopPropagation()}
              onChange={() => handleToggleAssignment(r.clinic.id, r.doctor)}
            />
          ) : null,
      },
      {
        key: "actions",
        header: "",
        width: "120px",
        render: (r) =>
          r.isFirstRow && r.clinic.id ? (
            <span style={{ display: "flex", flexDirection: "row", whiteSpace: "nowrap" }}>
              <IconButton
                size="small"
                color="default"
                onClick={(e) => {
                  e.stopPropagation();
                  openDialogForEdit(r.clinic.id);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="default"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClinic(r.clinic);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          ) : null,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openDialogForEdit]
  );

  return (
    <>
      <GenericInfiniteTable<ClinicDoctorRow>
        data={rows}
        columns={columns}
        hasMoreData={hasMore}
        fetchMoreData={fetchMoreData}
        isLoading={isInitialLoading}
        isFetchingNextPage={isFetchingNextPage}
        onRowClick={(r) => r.clinic.id && openDialogForEdit(r.clinic.id)}
        scrollId="clinicTableScroll"
        toolbar={toolbar}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        emptyMessage="Keine Praxen gefunden"
        // Rows of the same clinic share one shade; alternation is clinic by
        // clinic rather than row by row.
        getRowShadeIndex={(r) => r.clinicIndex}
        rowSx={(r) => (r.doctor?.isActive === false ? { opacity: 0.5 } : undefined)}
      />
      {loading && <LoadingSpinner />}

      <Dialog open={openDeleteClinicDialog} onClose={() => setOpenDeleteClinicDialog(false)}>
        <DialogTitle>Delete Clinic</DialogTitle>
        <DialogContent>
          Bist du sicher, du möchtest die Praxis {clinicToDelete?.businessName || clinicToDelete?.BSNR || ""} löschen?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteClinicDialog(false)} color="primary">
            abbrechen
          </Button>
          <Button color="secondary" onClick={handleDeleteClinicFunc}>
            löschen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
